import { NavLink } from 'react-router-dom';
import { Tooltip } from '@heroui/react';
import { visibleNavigation } from '@/config/navigation';
import { usePermission } from '@/hooks/usePermission';
import { useUI } from '@/contexts/UIContext';
import { env } from '@/config/env';
import { cn } from '@/lib/cn';
import { isFeatureEnabled } from '@/config/features';
import { usePlatformCapabilities } from '@/features/platform/hooks';
import { EnvelopeIcon, ChevronDoubleLeftIcon } from '@/icons';

interface SidebarProps {
  /** When true, render full width regardless of collapse (used inside mobile drawer). */
  forceExpanded?: boolean;
  /** Called when a link is chosen (mobile drawer closes on navigate). */
  onNavigate?: () => void;
}

/**
 * Primary navigation. Renders the declarative nav model, highlights the active
 * route, collapses to an icon rail on desktop (with tooltips), and is reused
 * verbatim inside the mobile drawer via `forceExpanded`.
 */
export function Sidebar({ forceExpanded = false, onNavigate }: SidebarProps) {
  const { sidebarCollapsed, toggleSidebar } = useUI();
  const { can } = usePermission();
  const { canOnboard } = usePlatformCapabilities();
  const collapsed = forceExpanded ? false : sidebarCollapsed;

  // Hide nav items the user lacks permission for (route guard is authoritative).
  const visibleGroups = visibleNavigation({ can, canOnboard });

  return (
    <div className="flex h-full flex-col bg-surface">
      {/* Brand + collapse control */}
      <div className={cn('flex h-16 items-center border-b border-border px-4', collapsed && 'justify-center px-0')}>
        <div className="flex items-center gap-2 overflow-hidden">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand text-brand-fg">
            <EnvelopeIcon className="h-5 w-5" aria-hidden="true" />
          </span>
          {!collapsed && <span className="truncate text-base font-semibold text-fg">{env.appName}</span>}
        </div>
      </div>

      {/* Nav groups */}
      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-4" aria-label="Primary">
        {visibleGroups.map((group) => (
          <div key={group.key}>
            {group.label && !collapsed && (
              <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-fg-subtle">
                {group.label}
              </p>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                const link = (
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        collapsed && 'justify-center px-0',
                        isActive
                          ? 'bg-brand/10 text-brand'
                          : 'text-fg-muted hover:bg-surface-raised hover:text-fg',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon className={cn('h-5 w-5 shrink-0', isActive ? 'text-brand' : '')} aria-hidden="true" />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                        {!collapsed && !isFeatureEnabled(item.key) && (
                          <span className="ml-auto rounded-full border border-border px-1.5 text-[10px] font-medium uppercase tracking-wide text-fg-subtle">
                            Soon
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
                return (
                  <li key={item.key}>
                    {collapsed ? (
                      <Tooltip content={item.label} placement="right" delay={200}>
                        {link}
                      </Tooltip>
                    ) : (
                      link
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Collapse toggle (desktop only) */}
      {!forceExpanded && (
        <div className="border-t border-border p-3">
          <button
            type="button"
            onClick={toggleSidebar}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-fg-muted hover:bg-surface-raised hover:text-fg',
              collapsed && 'justify-center px-0',
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronDoubleLeftIcon className={cn('h-5 w-5 transition-transform', collapsed && 'rotate-180')} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      )}
    </div>
  );
}
